// frontend/src/hooks/useItemSupplierLinks.js
//
// Every supplier relation one item holds — the read behind the per-item
// Suppliers dialog (components/supplier/ItemSuppliersDialog.jsx). The item
// grid's own row carries only the DEFAULT relation plus `related_supplier_ids`
// (ItemSerializer), which is enough to draw the cell but not to show, price or
// remove the other relations; those rows come from here.
//
// `/api/supplier-catalog-items/?item=` is DRF-paginated. One item rarely has
// more than a handful of suppliers, but "rarely" is not a bound, so the read
// walks every page through the shared pager and fails loudly rather than
// resolving short (utils/fetchAllPages.js) — a dialog listing three of an
// item's four suppliers would let the merchant "add" the fourth again.

import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../contexts/AuthContext';
import { sessionApi } from '../services/SessionApiService';
import { supplierCatalogItems as linksKey } from '../queries/keys';
import { fetchAllPagesVia } from '../utils/fetchAllPages';

const NO_LINKS = [];

/**
 * @param {number|string|null} itemId  null keeps the query idle (dialog closed)
 * @returns {{links: object[], isPending: boolean, isError: boolean}}
 *   `links` are SupplierCatalogItemSerializer rows: `{id, supplier: {id, name},
 *   supplier_sku, cost, currency, lead_time_days, supplier_sla_lead_time, moq,
 *   max_oq, order_multiple, is_default, origin}`.
 */
export function useItemSupplierLinks(itemId) {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const enabled = !!storeId && itemId != null;

  const query = useQuery({
    queryKey: linksKey.forItem(storeId, itemId),
    queryFn: () => fetchAllPagesVia(
      sessionApi, `/api/supplier-catalog-items/?item=${encodeURIComponent(itemId)}`,
      { label: 'item supplier links' },
    ),
    enabled,
  });

  return {
    links: query.data ?? NO_LINKS,
    // TanStack reports a disabled query as pending forever; the dialog is
    // closed then, and a spinner it would never clear must not be its state.
    isPending: enabled && query.isPending,
    isError: query.isError,
  };
}
